import { useCallback, useEffect, useState } from "react";
import type { Api } from "../api";

/**
 * T3 — "Enable notifications" for the inbox. Subscribes this browser to
 * Web Push for new chat activity; the toggle reflects the real subscription
 * state, not a stored preference.
 */

function keyToApplicationServerKey(b64url: string): Uint8Array {
  const b64 = b64url.replaceAll("-", "+").replaceAll("_", "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const raw = atob(b64 + pad);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function keyFromSubscription(sub: PushSubscription, name: "p256dh" | "auth") {
  const buf = sub.getKey(name);
  if (!buf) throw new Error(`subscription is missing ${name}`);
  let raw = "";
  for (const byte of new Uint8Array(buf)) raw += String.fromCharCode(byte);
  return btoa(raw)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

type PushState = "unsupported" | "checking" | "off" | "on" | "busy" | "blocked";

export function PushToggle({ api }: { api: Api }) {
  const [state, setState] = useState<PushState>("checking");

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setState("unsupported");
        return;
      }
      if (Notification.permission === "denied") {
        setState("blocked");
        return;
      }
      const registration = await navigator.serviceWorker.getRegistration();
      const sub = await registration?.pushManager.getSubscription();
      if (!cancelled) setState(sub ? "on" : "off");
    };
    void check().catch(() => setState("unsupported"));
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    setState("busy");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "blocked" : "off");
        return;
      }
      const registration =
        (await navigator.serviceWorker.getRegistration()) ??
        (await navigator.serviceWorker.register("/sw.js"));
      await navigator.serviceWorker.ready;
      const { key } = await api.pushKey();
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyToApplicationServerKey(key)
          .buffer as ArrayBuffer,
      });
      await api.pushSubscribe({
        endpoint: sub.endpoint,
        keys: {
          p256dh: keyFromSubscription(sub, "p256dh"),
          auth: keyFromSubscription(sub, "auth"),
        },
      });
      setState("on");
    } catch {
      setState("off");
    }
  }, [api]);

  const disable = useCallback(async () => {
    setState("busy");
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const sub = await registration?.pushManager.getSubscription();
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
        await sub.unsubscribe();
      }
      setState("off");
    } catch {
      setState("on");
    }
  }, [api]);

  if (state === "unsupported" || state === "checking") return null;
  if (state === "blocked") {
    return (
      <span
        className="muted small"
        title="Allow notifications for this site in your browser settings"
      >
        Notifications blocked
      </span>
    );
  }
  return (
    <button
      type="button"
      className="btn-secondary push-toggle"
      disabled={state === "busy"}
      onClick={() => void (state === "on" ? disable() : enable())}
      data-testid="push-toggle"
    >
      {state === "busy"
        ? "…"
        : state === "on"
          ? "Notifications on"
          : "Enable notifications"}
    </button>
  );
}
